import { useId, useRef, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Segmented, Tag } from 'antd';
import {
  AtIcon,
  EnvelopeSimpleIcon,
  GlobeIcon,
  LockIcon,
  PasswordIcon,
  PlusIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Input } from '../../Input';
import { colors, radii, spacing, typography } from '../../../theme-v2';
import { Button } from '../../Button';
import { ShareLinkRow } from '../ShareLinkRow';

export type ShareAccess = 'public' | 'password' | 'email';
export type ShareRestriction = 'people' | 'domain';

/** Same minimum as the classic publish flow (`VideoSharePasswordConfigComponent`). */
export const SHARE_PASSWORD_MIN_LENGTH = 8;

/** Long enough and confirmed — the only state that may be saved. */
export const isValidSharePassword = (password: string, confirm: string) =>
  password.length >= SHARE_PASSWORD_MIN_LENGTH && password === confirm;

export interface ShareAccessFieldsProps {
  access: ShareAccess;
  onAccessChange: (access: ShareAccess) => void;
  /** Shown read-only in both Public and Restricted modes. */
  shareUrl: string;
  restriction: ShareRestriction;
  onRestrictionChange: (restriction: ShareRestriction) => void;
  emailDraft: string;
  onEmailDraftChange: (value: string) => void;
  onAddEmail: () => void;
  emails: string[];
  onRemoveEmail: (email: string) => void;
  domain: string;
  onDomainChange: (domain: string) => void;
  onDomainBlur?: () => void;
  /** Fired with the raw selected file — the caller owns parsing (see the
   *  `parseCsvEmails` export) since it also owns how the result folds into
   *  `emails` (dedup, then either stage it for a batched Save or patch it
   *  right away). */
  onCsvFile: (file: File) => void;
  /** Feedback line shown under the Upload CSV button (import count / no
   *  valid emails found) — the caller composes this from its own i18n. */
  csvNote?: string;
  password: string;
  onPasswordChange: (password: string) => void;
  confirmPassword: string;
  onConfirmPasswordChange: (confirmPassword: string) => void;
  /** Fired on blur of either password field. */
  onPasswordBlur?: () => void;
  /** Inline error under the email input; the caller clears it on edit. */
  emailError?: string;
  /** Inline error under the domain input; the caller clears it on edit. */
  domainError?: string;
  /** Shown under the email or domain input, announced to screen readers. */
  validationError?: string;
}

/**
 * Public/Password/Email + People/Domain body — the visual and interaction core
 * of the experience-v2 Share modal (`ShareModal`), pulled out so a caller
 * that wants to commit per action (click/blur) instead of batching behind a
 * Save button can reuse the exact same UI. Fully controlled: no state here
 * beyond the hidden CSV `<input>`, everything else is lifted to the caller.
 */
export const ShareAccessFields = ({
  access,
  onAccessChange,
  shareUrl,
  restriction,
  onRestrictionChange,
  emailDraft,
  onEmailDraftChange,
  onAddEmail,
  emails,
  onRemoveEmail,
  domain,
  onDomainChange,
  onDomainBlur,
  onCsvFile,
  csvNote,
  password,
  onPasswordChange,
  confirmPassword,
  onConfirmPasswordChange,
  onPasswordBlur,
  emailError,
  domainError,
  validationError,
}: ShareAccessFieldsProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const fileRef = useRef<HTMLInputElement>(null);
  const passwordId = useId();
  const confirmPasswordId = useId();
  const emailErrorId = useId();
  const domainErrorId = useId();

  const modes: { value: ShareAccess; icon: ReactNode }[] = [
    { value: 'public', icon: <GlobeIcon size={18} weight="fill" /> },
    { value: 'password', icon: <PasswordIcon size={18} weight="fill" /> },
    { value: 'email', icon: <LockIcon size={18} weight="fill" /> },
  ];

  const isPasswordTooShort =
    password.length > 0 && password.length < SHARE_PASSWORD_MIN_LENGTH;
  // Both filled, like the classic publish flow.
  const isPasswordMismatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    confirmPassword !== password;

  const handleCsvChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onCsvFile(file);
    // Reset so selecting the same file again still fires onChange.
    event.target.value = '';
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: spacing.base }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: spacing.md,
        }}
      >
        {modes.map(({ value, icon }) => (
          <StyledModeCard
            key={value}
            type="button"
            aria-pressed={access === value}
            $active={access === value}
            onClick={() => onAccessChange(value)}
          >
            <StyledModeTitle $active={access === value}>
              {icon}
              {t(`experienceV2.share.mode.${value}.title`)}
            </StyledModeTitle>
            <StyledModeDesc>
              {t(`experienceV2.share.mode.${value}.description`)}
            </StyledModeDesc>
          </StyledModeCard>
        ))}
      </div>

      {access === 'public' ? (
        <>
          <ShareLinkRow
            label={t('experienceV2.share.public.linkLabel')}
            url={shareUrl}
          />
          <StyledNote>{t('experienceV2.share.public.note')}</StyledNote>
        </>
      ) : access === 'password' ? (
        <>
          <ShareLinkRow
            label={t('experienceV2.share.password.linkLabel')}
            url={shareUrl}
          />
          <StyledLabel htmlFor={passwordId}>
            {t('experienceV2.share.password.label')}
            <StyledLabelHint>
              {t('experienceV2.share.password.hint', {
                count: SHARE_PASSWORD_MIN_LENGTH,
              })}
            </StyledLabelHint>
          </StyledLabel>
          <Input
            id={passwordId}
            type="password"
            size="l"
            autoComplete="new-password"
            placeholder={t('experienceV2.share.password.placeholder')}
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            onBlur={onPasswordBlur}
            status={isPasswordTooShort ? 'error' : 'default'}
            helpText={
              isPasswordTooShort
                ? t('experienceV2.share.password.minLength', {
                    count: SHARE_PASSWORD_MIN_LENGTH,
                  })
                : undefined
            }
          />
          <StyledLabel htmlFor={confirmPasswordId}>
            {t('experienceV2.share.password.confirmLabel')}
          </StyledLabel>
          <Input
            id={confirmPasswordId}
            type="password"
            size="l"
            autoComplete="new-password"
            placeholder={t('experienceV2.share.password.placeholder')}
            value={confirmPassword}
            onChange={(event) => onConfirmPasswordChange(event.target.value)}
            onBlur={onPasswordBlur}
            status={isPasswordMismatch ? 'error' : 'default'}
            helpText={
              isPasswordMismatch
                ? t('experienceV2.share.password.mismatch')
                : undefined
            }
          />
          <StyledNote>{t('experienceV2.share.password.note')}</StyledNote>
        </>
      ) : (
        <>
          <ShareLinkRow
            label={t('experienceV2.share.email.linkLabel')}
            url={shareUrl}
          />
          <Segmented<ShareRestriction>
            block
            value={restriction}
            onChange={onRestrictionChange}
            options={[
              {
                value: 'people',
                label: (
                  <Flex align="center" justify="center" gap={spacing.xs}>
                    <EnvelopeSimpleIcon />
                    {t('experienceV2.share.restriction.people')}
                  </Flex>
                ),
              },
              {
                value: 'domain',
                label: (
                  <Flex align="center" justify="center" gap={spacing.xs}>
                    <AtIcon />
                    {t('experienceV2.share.restriction.domain')}
                  </Flex>
                ),
              },
            ]}
          />

          {restriction === 'people' ? (
            <>
              <StyledLabel>
                {t('experienceV2.share.people.inviteLabel')}
              </StyledLabel>
              <div
                style={{
                  display: 'flex',
                  gap: spacing.sm,
                  alignItems: 'center',
                }}
              >
                <Input
                  type="email"
                  size="l"
                  placeholder={t('experienceV2.share.people.placeholder')}
                  value={emailDraft}
                  onChange={(event) => onEmailDraftChange(event.target.value)}
                  onPressEnter={onAddEmail}
                  status={emailError ? 'error' : 'default'}
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? emailErrorId : undefined}
                />
                <Button
                  aria-label={t('experienceV2.share.people.add')}
                  variant="outlined"
                  size="l"
                  icon={<PlusIcon size={18} />}
                  onClick={onAddEmail}
                  style={{
                    borderRadius: radii.md,
                    borderColor: colors.border.subtle,
                    color: 'black',
                    padding: 0,
                    aspectRatio: 1,
                  }}
                />
              </div>
              {emailError ? (
                <StyledError id={emailErrorId} role="alert">
                  {emailError}
                </StyledError>
              ) : null}
              <div aria-live="polite">
                {validationError && (
                  <StyledErrorNote>{validationError}</StyledErrorNote>
                )}
              </div>
              {emails.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: spacing.sm,
                  }}
                >
                  {emails.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => onRemoveEmail(email)}
                      closeIcon={
                        <XIcon
                          size={12}
                          aria-label={t('experienceV2.share.people.remove', {
                            email,
                          })}
                        />
                      }
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCsvChange}
              />
              <Button
                variant="tertiary"
                size="l"
                icon={<UploadSimpleIcon size={16} />}
                onClick={() => fileRef.current?.click()}
                style={{
                  width: 'fit-content',
                  padding: 0,
                  fontWeight: 500,
                  color: 'black',
                }}
              >
                {t('experienceV2.share.people.uploadCsv')}
              </Button>
              {csvNote && <StyledNote>{csvNote}</StyledNote>}
            </>
          ) : (
            <>
              <StyledLabel>{t('experienceV2.share.domain.label')}</StyledLabel>
              <Input
                prefix="@"
                placeholder={t('experienceV2.share.domain.placeholder')}
                value={domain}
                onChange={(event) =>
                  onDomainChange(event.target.value.replace(/^@/, ''))
                }
                onBlur={onDomainBlur}
                status={domainError ? 'error' : 'default'}
                aria-invalid={domainError ? true : undefined}
                aria-describedby={domainError ? domainErrorId : undefined}
              />
              {domainError ? (
                <StyledError id={domainErrorId} role="alert">
                  {domainError}
                </StyledError>
              ) : null}
              <div aria-live="polite">
                {validationError && (
                  <StyledErrorNote>{validationError}</StyledErrorNote>
                )}
              </div>
              <StyledNote>
                {t('experienceV2.share.domain.note', {
                  domain: domain || t('experienceV2.share.domain.placeholder'),
                })}
              </StyledNote>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ShareAccessFields;

// ─── Styled components ───────────────────────────────────────────────────

const StyledModeCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  padding: ${spacing.base}px;
  text-align: left;
  cursor: pointer;
  border-radius: ${radii.box}px;
  border: 1.5px solid
    ${({ $active }) => ($active ? colors.accent.base : colors.border.subtle)};
  background: ${({ $active }) =>
    $active ? colors.accent.tint : colors.surface.base};
`;

const StyledModeTitle = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm}px;
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ $active }) =>
    $active ? colors.accent.active : colors.text.primary};
`;

const StyledModeDesc = styled.span`
  font-size: ${typography.size.sm}px;
  line-height: ${typography.lineHeight.medium};
  color: ${colors.text.secondary};
`;

const StyledLabel = styled.label`
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${colors.text.primary};
`;

const StyledLabelHint = styled.span`
  margin-left: ${spacing.xs}px;
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.regular};
  color: ${colors.text.secondary};
`;

const StyledNote = styled.p`
  margin: 0;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.lineHeight.body};
  color: ${colors.text.secondary};
`;

const StyledError = styled.p`
  margin: 0;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.lineHeight.body};
  color: ${colors.status.error};
`;

const StyledErrorNote = styled(StyledNote)`
  color: ${colors.status.error};
`;
